import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiUrl } from '../../lib/api';

// THE SCREENSHOT, AND THE CARD, AS THE DESK SEES THEM (deposit-proof AC9,
// AC15). The image is read through the authenticated route and never a public
// URL (§9); in place it is large enough to read, and a tap opens it full
// screen over everything, which a second tap or Escape closes.

export const proofImageUrl = (id: string) => apiUrl(`/proofs/${encodeURIComponent(id)}/image`);

// A PDF IS NOT AN IMAGE (PR #255 review, #06/#16/#53). PDF is an accepted
// upload type, and an <img> cannot decode it, so Patricia saw a blank where the
// transfer should be. A PDF opens in the browser's own viewer through a
// same-origin <iframe> — never <object>, which the site's CSP refuses with
// `object-src 'none'` — with a plain link beside it for a browser (iOS) that
// shows only the first page in a frame.
export const isPdf = (mime?: string | null) => mime === 'application/pdf';

function PdfView({ id, alt }: { id: string; alt: string }) {
  const url = proofImageUrl(id);
  return (
    <span className="proof-pdf">
      <iframe src={url} title={alt} />
      <a href={url} target="_blank" rel="noopener noreferrer" className="proof-pdf-open">Open the PDF</a>
    </span>
  );
}

export function ProofFull({ id, alt, mime, onClose }: { id: string; alt: string; mime?: string | null; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => { box.current?.focus(); }, []);
  // OVER EVERYTHING, FROM THE BODY. The Self-service column scrolls and the
  // modal slides in on a transform, so a `fixed` box inside either is clipped
  // to it; the portal puts the viewer where fixed means the viewport.
  const full = (
    <div className="proof-full" role="dialog" aria-label={alt} ref={box} onClick={onClose} onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }} tabIndex={-1}>
      {isPdf(mime) ? <span onClick={(e) => e.stopPropagation()}><PdfView id={id} alt={alt} /></span> : <img src={proofImageUrl(id)} alt={alt} />}
    </div>
  );
  return typeof document === 'undefined' ? full : createPortal(full, document.body);
}

export function ProofImage({ id, alt = 'The screenshot', muted = false, mime }: { id: string; alt?: string; muted?: boolean; mime?: string | null }) {
  const [full, setFull] = useState(false);
  if (isPdf(mime)) return <span className={'proof-img is-pdf' + (muted ? ' is-muted' : '')}><PdfView id={id} alt={alt} /></span>;
  return (
    <>
      <button type="button" className={'proof-img' + (muted ? ' is-muted' : '')} aria-label="Open the screenshot full screen" onClick={() => setFull(true)}>
        <img src={proofImageUrl(id)} alt={alt} />
      </button>
      {full && <ProofFull id={id} alt={alt} onClose={() => setFull(false)} />}
    </>
  );
}
